import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, TrendingUp, Eye, Users, Mail, Heart, 
  UserPlus, Download, Calendar, DollarSign, Activity,
  BarChart3, Clock
} from 'lucide-react';
import './AdminDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [newsStats, setNewsStats] = useState({
    totalNews: 0,
    publishedNews: 0,
    draftNews: 0,
    recentNews: []
  });
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    loadAllStats();
  }, []);

  const loadAllStats = async () => {
    try {
      // Load news stats
      const newsResponse = await axios.get(`${API}/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const news = newsResponse.data;
      const published = news.filter(n => n.published).length;
      
      setNewsStats({
        totalNews: news.length,
        publishedNews: published,
        draftNews: news.length - published,
        recentNews: news.slice(0, 5)
      });

      // Load dashboard statistics
      const statsResponse = await axios.get(`${API}/statistics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(statsResponse.data);

    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async (type) => {
    setExporting(type);
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar:', error);
      alert('Erro ao exportar dados');
    } finally {
      setExporting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">A carregar...</div>;
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Visão geral da gestão de conteúdos e submissões</p>
      </div>

      {/* Summary Stats */}
      <div className="dashboard-section">
        <h2 className="section-title">
          <Activity size={20} />
          Resumo Geral
        </h2>
        <div className="stats-grid stats-grid-6">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e3f2fd' }}>
              <Mail size={24} color="#1976d2" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{dashboardStats?.summary?.total_contacts || 0}</div>
              <div className="stat-label">Contactos</div>
              <div className="stat-pending">{dashboardStats?.summary?.pending_contacts || 0} novos</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e8f5e9' }}>
              <Heart size={24} color="#388e3c" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{dashboardStats?.summary?.total_donations || 0}</div>
              <div className="stat-label">Donativos</div>
              <div className="stat-pending">{dashboardStats?.summary?.pending_donations || 0} pendentes</div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fff3e0' }}>
              <UserPlus size={24} color="#f57c00" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{dashboardStats?.summary?.total_memberships || 0}</div>
              <div className="stat-label">Sócios</div>
              <div className="stat-pending">{dashboardStats?.summary?.pending_memberships || 0} pendentes</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e3f2fd' }}>
              <FileText size={24} color="#1976d2" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{newsStats.totalNews}</div>
              <div className="stat-label">Notícias</div>
              <div className="stat-pending">{newsStats.publishedNews} publicadas</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f3e5f5' }}>
              <DollarSign size={24} color="#7b1fa2" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{dashboardStats?.financial?.total_payments_count || 0}</div>
              <div className="stat-label">Pagamentos</div>
              <div className="stat-pending">{(dashboardStats?.financial?.total_payments_amount || 0).toFixed(2)}€</div>
            </div>
          </div>

          <div className="stat-card highlight">
            <div className="stat-icon" style={{ background: '#e8f5e9' }}>
              <TrendingUp size={24} color="#388e3c" />
            </div>
            <div className="stat-content">
              <div className="stat-value">{(dashboardStats?.financial?.total_donations_amount || 0).toFixed(2)}€</div>
              <div className="stat-label">Total Donativos</div>
              <div className="stat-pending">Valor acumulado</div>
            </div>
          </div>
        </div>
      </div>

      {/* Time-based Stats */}
      <div className="dashboard-section">
        <h2 className="section-title">
          <Calendar size={20} />
          Atividade Recente
        </h2>
        <div className="stats-grid">
          <div className="stat-card time-card">
            <div className="time-header">
              <Clock size={18} />
              <span>Últimos 7 dias</span>
            </div>
            <div className="time-stats">
              <div className="time-stat">
                <span className="time-value">{dashboardStats?.last_7_days?.contacts || 0}</span>
                <span className="time-label">Contactos</span>
              </div>
              <div className="time-stat">
                <span className="time-value">{dashboardStats?.last_7_days?.donations || 0}</span>
                <span className="time-label">Donativos</span>
              </div>
              <div className="time-stat">
                <span className="time-value">{dashboardStats?.last_7_days?.memberships || 0}</span>
                <span className="time-label">Sócios</span>
              </div>
            </div>
          </div>
          
          <div className="stat-card time-card">
            <div className="time-header">
              <BarChart3 size={18} />
              <span>Últimos 30 dias</span>
            </div>
            <div className="time-stats">
              <div className="time-stat">
                <span className="time-value">{dashboardStats?.last_30_days?.contacts || 0}</span>
                <span className="time-label">Contactos</span>
              </div>
              <div className="time-stat">
                <span className="time-value">{dashboardStats?.last_30_days?.donations || 0}</span>
                <span className="time-label">Donativos</span>
              </div>
              <div className="time-stat">
                <span className="time-value">{dashboardStats?.last_30_days?.memberships || 0}</span>
                <span className="time-label">Sócios</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="dashboard-section">
        <h2 className="section-title">
          <Download size={20} />
          Exportar Dados (CSV)
        </h2>
        <div className="export-buttons">
          <button 
            className="export-btn" 
            onClick={() => handleExportCSV('contacts')}
            disabled={exporting === 'contacts'}
            data-testid="export-contacts-btn"
          >
            <Mail size={18} />
            {exporting === 'contacts' ? 'A exportar...' : 'Exportar Contactos'}
          </button>
          <button 
            className="export-btn" 
            onClick={() => handleExportCSV('donations')}
            disabled={exporting === 'donations'}
            data-testid="export-donations-btn"
          >
            <Heart size={18} />
            {exporting === 'donations' ? 'A exportar...' : 'Exportar Donativos'}
          </button>
          <button 
            className="export-btn" 
            onClick={() => handleExportCSV('memberships')}
            disabled={exporting === 'memberships'}
            data-testid="export-memberships-btn"
          >
            <UserPlus size={18} />
            {exporting === 'memberships' ? 'A exportar...' : 'Exportar Sócios'}
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="dashboard-section">
        <h2 className="section-title">
          <Activity size={20} />
          Atividade Recente
        </h2>
        <div className="activity-grid">
          {/* Recent Contacts */}
          <div className="activity-card">
            <h3><Mail size={16} /> Últimos Contactos</h3>
            <div className="activity-list">
              {dashboardStats?.recent_activity?.contacts?.length > 0 ? (
                dashboardStats.recent_activity.contacts.map((item, idx) => (
                  <div key={idx} className="activity-item">
                    <div className="activity-info">
                      <span className="activity-name">{item.nome}</span>
                      <span className="activity-email">{item.email}</span>
                    </div>
                    <div className="activity-meta">
                      <span className={`activity-status ${item.status}`}>{item.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-activity">Sem contactos recentes</p>
              )}
            </div>
            <Link to="/admin/dashboard/submissions" className="view-all-link">Ver todos</Link>
          </div>

          {/* Recent Donations */}
          <div className="activity-card">
            <h3><Heart size={16} /> Últimos Donativos</h3>
            <div className="activity-list">
              {dashboardStats?.recent_activity?.donations?.length > 0 ? (
                dashboardStats.recent_activity.donations.map((item, idx) => (
                  <div key={idx} className="activity-item">
                    <div className="activity-info">
                      <span className="activity-name">{item.nome}</span>
                      <span className="activity-amount">{item.valor}€</span>
                    </div>
                    <div className="activity-meta">
                      <span className={`activity-status ${item.status}`}>{item.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-activity">Sem donativos recentes</p>
              )}
            </div>
            <Link to="/admin/dashboard/submissions" className="view-all-link">Ver todos</Link>
          </div>

          {/* Recent Memberships */}
          <div className="activity-card">
            <h3><UserPlus size={16} /> Últimos Pedidos de Sócio</h3>
            <div className="activity-list">
              {dashboardStats?.recent_activity?.memberships?.length > 0 ? (
                dashboardStats.recent_activity.memberships.map((item, idx) => (
                  <div key={idx} className="activity-item">
                    <div className="activity-info">
                      <span className="activity-name">{item.nome_completo}</span>
                      <span className="activity-email">{item.email}</span>
                    </div>
                    <div className="activity-meta">
                      <span className={`activity-status ${item.status}`}>{item.status}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="empty-activity">Sem pedidos recentes</p>
              )}
            </div>
            <Link to="/admin/dashboard/submissions" className="view-all-link">Ver todos</Link>
          </div>
        </div>
      </div>
      
      {/* News Section */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2 className="section-title">
            <FileText size={20} />
            Notícias Recentes
          </h2>
          <Link to="/admin/dashboard/news" className="view-all-btn">
            Ver todas
          </Link>
        </div>
        
        <div className="recent-news-list">
          {newsStats.recentNews.length === 0 ? (
            <div className="empty-state">
              <FileText size={48} color="#ccc" />
              <p>Ainda não existem notícias</p>
              <Link to="/admin/dashboard/news/new" className="create-first-btn">
                Criar primeira notícia
              </Link>
            </div>
          ) : (
            newsStats.recentNews.map(news => (
              <div key={news.id} className="recent-news-item">
                {news.image_url && (
                  <div className="news-thumb">
                    <img src={news.image_url} alt={news.title} />
                  </div>
                )}
                <div className="news-info">
                  <h3>{news.title}</h3>
                  <p>{news.description}</p>
                  <div className="news-meta">
                    <span className={`status ${news.published ? 'published' : 'draft'}`}>
                      {news.published ? 'Publicada' : 'Rascunho'}
                    </span>
                    <span className="date">
                      {new Date(news.created_at).toLocaleDateString('pt-PT')}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
